import { useState, useEffect } from 'react';
import { api } from '../services/baseUrl';

const BASE_API =
  process.env.REACT_APP_API_BASE_URL || api?.defaults?.baseURL || 'https://api.krayneagency.fr/api';
const API_ROOT = BASE_API.replace(/\/api\/?$/, '/');

// Helper pour construire URL complète de l'image
// IMPORTANT: Toutes les images passent par /api/images/ pour appliquer le watermark
const getImageUrl = (imageData) => {
  if (!imageData) return null;
  
  // Nettoyer le chemin de l'image
  let cleanPath = String(imageData);
  
  // Si c'est déjà une URL complète, extraire le chemin
  if (cleanPath.startsWith('http')) {
    cleanPath = cleanPath.replace(/^https?:\/\/[^/]+\//, '');
  }
  
  // Retirer les slashes au début
  cleanPath = cleanPath.replace(/^\/+/, '');
  
  // Retirer 'storage/' si présent (déjà dans le chemin du disque)
  if (cleanPath.startsWith('storage/')) {
    cleanPath = cleanPath.substring(8);
  }
  
  // Retirer 'api/images/' si déjà présent pour éviter duplication
  if (cleanPath.startsWith('api/images/')) {
    cleanPath = cleanPath.substring(11);
  }
  
  // Retourner via l'endpoint protégé
  return `${API_ROOT}api/images/${cleanPath}`;
};

const getImagesArray = (product) => {
  const rawImages = Array.isArray(product?.images) ? product.images : [];

  return rawImages
    .map((img) => {
      if (!img) return null;
      if (typeof img === 'string') return getImageUrl(img);

      return getImageUrl(img.path_watermarked ?? img.path_image ?? img.image_url ?? img.url ?? null);
    })
    .filter(Boolean);
};

export const useProducts = () => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Récupérer les catégories
        const categoriesResponse = await api.get('/categories');
        const categoriesRaw = Array.isArray(categoriesResponse.data)
          ? categoriesResponse.data
          : (categoriesResponse.data?.data || []);
        if (Array.isArray(categoriesRaw)) {
          setCategories(categoriesRaw);
        }
        
        // Récupérer les produits publics
        const productsResponse = await api.get('/products');
        // Accepter plusieurs formats de réponse: {data: [...]}, [...], {products: [...]}
        const raw = Array.isArray(productsResponse.data)
          ? productsResponse.data
          : (productsResponse.data?.data || productsResponse.data?.products || []);
        if (Array.isArray(raw)) {
          const mappedProducts = raw.map((p) => ({
            id: p.id ?? p.id_product,
            id_product: p.id_product ?? p.id,
            title: p.name ?? p.name_product ?? '',
            name: p.name ?? p.name_product ?? '',
            description: p.description ?? p.description_product ?? '',
            category: p.category ?? p.category_name ?? '',
            price: p.price ?? p.price_product ?? p.price_ttc ?? 0,
            price_product: p.price ?? p.price_product ?? 0,
            price_ht: p.price ?? p.price_product ?? 0,
            price_ttc: p.price_ttc ?? p.price ?? 0,
            promo_active: p.promo_active ?? false,
            promo_discount: p.promo_discount ?? null,
            price_promo: p.price_promo ?? null,
            price_promo_ttc: p.price_promo_ttc ?? null,
            stock: p.available_stock ?? p.stock ?? 0,
            sale_type: p.sale_type ?? 'libre',
            status: p.status ?? 'active',
            image: getImageUrl(p.image ?? p.image_url ?? null),
            images: getImagesArray(p),
            creator_type: p.creator_type ?? 'krayne',
            creator_name: p.creator_name ?? 'Krayne Agency',
          }));
          setProducts(mappedProducts);
        }
        
        setError(null);
      } catch (err) {
        console.error('Erreur lors de la récupération des produits:', err);
        setError('Erreur lors du chargement des produits');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  return {
    products,
    categories,
    loading,
    error,
  };
};

// Hook spécifique pour le portfolio/creations
// Montre TOUS les produits (libre ET unique) contrairement à useProducts qui filtre pour la boutique
export const usePortfolioProducts = () => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Récupérer les catégories
        const categoriesResponse = await api.get('/categories');
        const categoriesRaw = Array.isArray(categoriesResponse.data)
          ? categoriesResponse.data
          : (categoriesResponse.data?.data || []);
        if (Array.isArray(categoriesRaw)) {
          setCategories(categoriesRaw);
        }
        
        // Récupérer TOUS les produits actifs pour le portfolio
        const productsResponse = await api.get('/products/portfolio');
        const raw = Array.isArray(productsResponse.data)
          ? productsResponse.data
          : (productsResponse.data?.data || productsResponse.data?.products || []);
        if (Array.isArray(raw)) {
          const mappedProducts = raw.map((p) => ({
            id: p.id ?? p.id_product,
            id_product: p.id_product ?? p.id,
            title: p.name ?? p.name_product ?? '',
            name: p.name ?? p.name_product ?? '',
            description: p.description ?? p.description_product ?? '',
            category: p.category ?? p.category_name ?? '',
            price: p.price ?? p.price_product ?? p.price_ttc ?? 0,
            price_product: p.price ?? p.price_product ?? 0,
            price_ht: p.price ?? p.price_product ?? 0,
            price_ttc: p.price_ttc ?? p.price ?? 0,
            promo_active: p.promo_active ?? false,
            promo_discount: p.promo_discount ?? null,
            price_promo: p.price_promo ?? null,
            price_promo_ttc: p.price_promo_ttc ?? null,
            stock: p.available_stock ?? p.stock ?? 0,
            sale_type: p.sale_type ?? 'libre',
            status: p.status ?? 'active',
            image: getImageUrl(p.image ?? p.image_url ?? null),
            images: getImagesArray(p),
            creator_type: p.creator_type ?? 'krayne',
            creator_name: p.creator_name ?? 'Krayne Agency',
          }));
          setProducts(mappedProducts);
        }
        
        setError(null);
      } catch (err) {
        console.error('Erreur lors de la récupération du portfolio:', err);
        setError('Erreur lors du chargement du portfolio');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  return {
    products,
    categories,
    loading,
    error,
  };
};

export const useProductsByCategory = (categoryId) => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!categoryId) return;

    const fetchProducts = async () => {
      try {
        setLoading(true);
        const response = await api.get(`/categories/${categoryId}`);
        
        if (response.data?.data?.products) {
          const mappedProducts = response.data.data.products.map((p) => ({
            ...p,
            image: getImageUrl(p.image ?? p.image_url ?? null),
          }));
          setProducts(mappedProducts);
        }
        
        setError(null);
      } catch (err) {
        console.error('Erreur lors de la récupération des produits:', err);
        setError('Erreur lors du chargement des produits');
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, [categoryId]);

  return {
    products,
    loading,
    error,
  };
};

export const useProductById = (productId) => {
  const [product, setProduct] = useState(null);
  const [allRatings, setAllRatings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [forbidden, setForbidden] = useState(false);

  const fetchProduct = async () => {
    if (!productId) return;
    try {
      setLoading(true);
      setNotFound(false);
      setForbidden(false);
      const response = await api.get(`/products/${productId}`);
      if (response.data?.data) {
        const mappedProduct = {
          ...response.data.data,
          image: getImageUrl(response.data.data.image ?? response.data.data.image_url ?? null),
          images: getImagesArray(response.data.data),
        };
        setProduct(mappedProduct);
        setNotFound(false);
        // Récupérer la liste des avis si présente
        if (Array.isArray(response.data.data.all_ratings)) {
          setAllRatings(response.data.data.all_ratings);
        } else {
          setAllRatings([]);
        }
      }
      setError(null);
    } catch (err) {
      setProduct(null);
      setAllRatings([]);

      if (err?.response?.status === 404) {
        setNotFound(true);
        setForbidden(false);
        setError(null);
      } else if (err?.response?.status === 403) {
        setForbidden(true);
        setNotFound(false);
        setError(null);
      } else {
        console.error('Erreur lors de la récupération du produit:', err);
        setNotFound(false);
        setForbidden(false);
        setError('Erreur lors du chargement du produit');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProduct();
  }, [productId]);

  return {
    product,
    allRatings,
    loading,
    error,
    notFound,
    forbidden,
    refetch: fetchProduct, // Permet de rafraîchir le produit manuellement
  };
};
